/**
 * Generate a random RFC4122 v4-shaped identifier.
 *
 * Wraps `crypto.randomUUID()` when available (modern browsers and Node 19+)
 * with a `Math.random()` fallback for environments where `crypto` is not
 * exposed as a global — notably the jsdom test runtime under
 * react-scripts 5, where `crypto is not defined` at module top-level.
 *
 * Replaces the `uuid4` helper from `@sentry/utils`, which was made
 * internal/removed from the public API in `@sentry/react@8`.
 */
const randomId = (): string => {
  const cryptoApi =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    // For 'y' positions, force the high two bits to '10' (i.e. 8–11) to
    // produce a valid RFC4122 v4 variant without using bitwise operators.
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

export default randomId;
